import omit from 'lodash/omit';
import { types as sdkTypes } from '../../util/sdkLoader';
import { denormalisedResponseEntities, ensureAvailabilityException } from '../../util/data';
import { isSameDate, monthIdStringInUTC } from '../../util/dates';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  jobPostingChat as jobPostingChatStream,
  jobPostingDraft as jobPostingDraftStream,
} from '../../util/api';
import { trackIntercomEvent, updateIntercomUser } from '../../util/intercom';

const { UUID } = sdkTypes;

// A helper function to filter away exception that matches start and end timestamps
const removeException = (exception, calendar) => {
  const availabilityException = ensureAvailabilityException(exception.availabilityException);
  const { start, end } = availabilityException.attributes;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);
  const monthData = calendar[monthId] || { exceptions: [] };

  const exceptions = monthData.exceptions.filter(e => {
    const anException = ensureAvailabilityException(e.availabilityException);
    const exceptionStart = anException.attributes.start;
    const exceptionEnd = anException.attributes.end;

    return !(isSameDate(exceptionStart, start) && isSameDate(exceptionEnd, end));
  });

  return {
    ...calendar,
    [monthId]: { ...monthData, exceptions },
  };
};

// A helper function to add a new exception and remove previous one if there's a matching exception
const addException = (exception, calendar) => {
  const { start } = ensureAvailabilityException(exception.availabilityException).attributes;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);

  // TODO: API doesn't support "availability_exceptions/update" yet
  // So, when user wants to create an exception we need to ensure
  // that possible existing exception is removed first.
  const cleanCalendar = removeException(exception, calendar);
  const monthData = cleanCalendar[monthId] || { exceptions: [] };

  return {
    ...cleanCalendar,
    [monthId]: { ...monthData, exceptions: [...monthData.exceptions, exception] },
  };
};

// A helper function to update exception that matches start and end timestamps
const updateException = (exception, calendar) => {
  const newAvailabilityException = ensureAvailabilityException(exception.availabilityException);
  const { start, end } = newAvailabilityException.attributes;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);
  const monthData = calendar[monthId] || { exceptions: [] };

  const exceptions = monthData.exceptions.map(e => {
    const availabilityException = ensureAvailabilityException(e.availabilityException);
    const exceptionStart = availabilityException.attributes.start;
    const exceptionEnd = availabilityException.attributes.end;

    return isSameDate(exceptionStart, start) && isSameDate(exceptionEnd, end) ? exception : e;
  });

  return {
    ...calendar,
    [monthId]: { ...monthData, exceptions },
  };
};

// Update calendar data of given month
const updateCalendarMonth = (state, monthId, data) => {
  // Ensure that every month has array for bookings and exceptions
  const defaultMonthData = { bookings: [], exceptions: [] };
  return {
    ...state,
    availabilityCalendar: {
      ...state.availabilityCalendar,
      [monthId]: {
        ...defaultMonthData,
        ...state.availabilityCalendar[monthId],
        ...data,
      },
    },
  };
};

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditProjectListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditProjectListingPage/CLEAR_UPDATED_TAB';

export const CREATE_PROJECT_DRAFT_REQUEST =
  'app/EditProjectListingPage/CREATE_PROJECT_DRAFT_REQUEST';
export const CREATE_PROJECT_DRAFT_SUCCESS =
  'app/EditProjectListingPage/CREATE_PROJECT_DRAFT_SUCCESS';
export const CREATE_PROJECT_DRAFT_ERROR = 'app/EditProjectListingPage/CREATE_PROJECT_DRAFT_ERROR';

export const PUBLISH_PROJECT_REQUEST = 'app/EditProjectListingPage/PUBLISH_PROJECT_REQUEST';
export const PUBLISH_PROJECT_SUCCESS = 'app/EditProjectListingPage/PUBLISH_PROJECT_SUCCESS';
export const PUBLISH_PROJECT_ERROR = 'app/EditProjectListingPage/PUBLISH_PROJECT_ERROR';

export const UPDATE_PROJECT_REQUEST = 'app/EditProjectListingPage/UPDATE_PROJECT_REQUEST';
export const UPDATE_PROJECT_SUCCESS = 'app/EditProjectListingPage/UPDATE_PROJECT_SUCCESS';
export const UPDATE_PROJECT_ERROR = 'app/EditProjectListingPage/UPDATE_PROJECT_ERROR';

export const SHOW_PROJECTS_REQUEST = 'app/EditProjectListingPage/SHOW_PROJECTS_REQUEST';
export const SHOW_PROJECTS_SUCCESS = 'app/EditProjectListingPage/SHOW_PROJECTS_SUCCESS';
export const SHOW_PROJECTS_ERROR = 'app/EditProjectListingPage/SHOW_PROJECTS_ERROR';

export const FETCH_BOOKINGS_REQUEST = 'app/EditProjectListingPage/FETCH_BOOKINGS_REQUEST';
export const FETCH_BOOKINGS_SUCCESS = 'app/EditProjectListingPage/FETCH_BOOKINGS_SUCCESS';
export const FETCH_BOOKINGS_ERROR = 'app/EditProjectListingPage/FETCH_BOOKINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST =
  'app/EditProjectListingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS =
  'app/EditProjectListingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR =
  'app/EditProjectListingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';

export const CREATE_EXCEPTION_REQUEST =
  'app/EditProjectListingPage/CREATE_AVAILABILITY_EXCEPTION_REQUEST';
export const CREATE_EXCEPTION_SUCCESS =
  'app/EditProjectListingPage/CREATE_AVAILABILITY_EXCEPTION_SUCCESS';
export const CREATE_EXCEPTION_ERROR =
  'app/EditProjectListingPage/CREATE_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST =
  'app/EditProjectListingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS =
  'app/EditProjectListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR =
  'app/EditProjectListingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditProjectListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditProjectListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditProjectListingPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_IMAGE_ORDER = 'app/EditProjectListingPage/UPDATE_IMAGE_ORDER';

export const REMOVE_PROJECT_IMAGE = 'app/EditProjectListingPage/REMOVE_PROJECT_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditProjectListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditProjectListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditProjectListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const CHAT_APPEND_USER = 'app/EditProjectListingPage/CHAT_APPEND_USER';
export const CHAT_STREAM_START = 'app/EditProjectListingPage/CHAT_STREAM_START';
export const CHAT_STREAM_DELTA = 'app/EditProjectListingPage/CHAT_STREAM_DELTA';
export const CHAT_STREAM_DONE = 'app/EditProjectListingPage/CHAT_STREAM_DONE';
export const CHAT_STREAM_ERROR = 'app/EditProjectListingPage/CHAT_STREAM_ERROR';
export const CHAT_SESSION_CREATED = 'app/EditProjectListingPage/CHAT_SESSION_CREATED';
export const CHAT_HYDRATE = 'app/EditProjectListingPage/CHAT_HYDRATE';
export const CHAT_RESET = 'app/EditProjectListingPage/CHAT_RESET';

export const DRAFT_STREAM_START = 'app/EditProjectListingPage/DRAFT_STREAM_START';
export const DRAFT_STREAM_DELTA = 'app/EditProjectListingPage/DRAFT_STREAM_DELTA';
export const DRAFT_STREAM_DONE = 'app/EditProjectListingPage/DRAFT_STREAM_DONE';
export const DRAFT_STREAM_ERROR = 'app/EditProjectListingPage/DRAFT_STREAM_ERROR';
export const DRAFT_RESET = 'app/EditProjectListingPage/DRAFT_RESET';

export const SAVE_LISTING_DRAFT_REQUEST = 'app/EditProjectListingPage/SAVE_LISTING_DRAFT_REQUEST';
export const SAVE_LISTING_DRAFT_SUCCESS = 'app/EditProjectListingPage/SAVE_LISTING_DRAFT_SUCCESS';
export const SAVE_LISTING_DRAFT_ERROR = 'app/EditProjectListingPage/SAVE_LISTING_DRAFT_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  publishingListing: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  availabilityCalendar: {
    // '2018-12': {
    //   bookings: [],
    //   exceptions: [],
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: false,
    //   fetchBookingsError: null,
    //   fetchBookingsInProgress: false,
    // },
  },
  images: {},
  imageOrder: [],
  removedImageIds: [],
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  savingDraft: false,
  saveListingDraftError: null,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  jobPostingChat: {
    transcript: [],
    sessionId: null,
    agent1Done: false,
    // Collaboration type ("project" | "continuous") the intake agent inferred
    // from the conversation. Used to prefill projectDuration on the draft step
    // instead of asking the user inline.
    projectDuration: null,
    isStreaming: false,
    streamingText: '',
    error: null,
  },
  jobPostingDraft: {
    isStreaming: false,
    streamingText: '',
    fields: null,
    error: null,
  },
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_PROJECT_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_PROJECT_DRAFT_SUCCESS:
      return {
        ...state,
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_PROJECT_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_PROJECT_REQUEST:
      return {
        ...state,
        publishingListing: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_PROJECT_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        publishingListing: null,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_PROJECT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishingListing: null,
        publishListingError: {
          listingId: state.publishingListing,
          error: payload,
        },
      };
    }

    case UPDATE_PROJECT_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_PROJECT_SUCCESS:
      return { ...state, updateInProgress: false };
    case UPDATE_PROJECT_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SAVE_LISTING_DRAFT_REQUEST:
      return { ...state, savingDraft: true, saveListingDraftError: null };
    case SAVE_LISTING_DRAFT_SUCCESS:
      return { ...state, savingDraft: false };
    case SAVE_LISTING_DRAFT_ERROR:
      return { ...state, savingDraft: false, saveListingDraftError: payload };

    case SHOW_PROJECTS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_PROJECTS_SUCCESS:
      // Reset the page slice, but preserve jobPostingDraft AND jobPostingChat so
      // an in-progress conversation / writer stream survives the loadData that
      // fires on a route change (e.g. the new->draft URL replace done mid-chat to
      // make the draft reload-safe). Without preserving jobPostingChat the chat
      // panel would briefly flash its empty state before CHAT_HYDRATE restores
      // the transcript. loadData clears each explicitly when the loaded listing
      // isn't an in-progress chat/draft (see below).
      return {
        ...initialState,
        availabilityCalendar: { ...state.availabilityCalendar },
        jobPostingDraft: state.jobPostingDraft,
        jobPostingChat: state.jobPostingChat,
      };

    case SHOW_PROJECTS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case FETCH_BOOKINGS_REQUEST:
      return updateCalendarMonth(state, payload.params.monthId, {
        fetchBookingsError: null,
        fetchBookingsInProgress: true,
      });
    case FETCH_BOOKINGS_SUCCESS:
      return updateCalendarMonth(state, payload.monthId, {
        bookings: payload.bookings,
        fetchBookingsInProgress: false,
      });
    case FETCH_BOOKINGS_ERROR:
      return updateCalendarMonth(state, payload.monthId, {
        fetchBookingsError: payload.error,
        fetchBookingsInProgress: false,
      });

    case FETCH_EXCEPTIONS_REQUEST:
      return updateCalendarMonth(state, payload.params.monthId, {
        fetchExceptionsError: null,
        fetchExceptionsInProgress: true,
      });
    case FETCH_EXCEPTIONS_SUCCESS:
      return updateCalendarMonth(state, payload.monthId, {
        exceptions: payload.exceptions,
        fetchExceptionsInProgress: false,
      });
    case FETCH_EXCEPTIONS_ERROR:
      return updateCalendarMonth(state, payload.monthId, {
        fetchExceptionsError: payload.error,
        fetchExceptionsInProgress: false,
      });

    case CREATE_EXCEPTION_REQUEST: {
      const { start, end, seats } = payload.params;
      const draft = ensureAvailabilityException({ attributes: { start, end, seats } });
      const exception = { availabilityException: draft, inProgress: true };
      const availabilityCalendar = addException(exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case CREATE_EXCEPTION_SUCCESS: {
      const availabilityCalendar = updateException(payload.exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case CREATE_EXCEPTION_ERROR: {
      const { availabilityException, error } = payload;
      const failedException = { availabilityException, error };
      const availabilityCalendar = updateException(failedException, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }

    case DELETE_EXCEPTION_REQUEST: {
      const { id, seats, currentException } = payload.params;

      // We first create temporary exception with given 'seats' count (the default after deletion).
      // This makes it possible to show the UI element immediately with default color that matches
      // with the availability plan.
      const exception = {
        id,
        inProgress: true,
        availabilityException: {
          ...currentException.availabilityException,
          attributes: { ...currentException.availabilityException.attributes, seats },
        },
      };

      const availabilityCalendar = updateException(exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case DELETE_EXCEPTION_SUCCESS: {
      const availabilityCalendar = removeException(payload.exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case DELETE_EXCEPTION_ERROR: {
      const { availabilityException, error } = payload;
      const failedException = { availabilityException, error };
      const availabilityCalendar = updateException(failedException, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const images = {
        ...state.images,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        images,
        imageOrder: state.imageOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id'}
      const { id, imageId } = payload;
      const file = state.images[id].file;
      const images = { ...state.images, [id]: { id, imageId, file } };
      return { ...state, images };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const imageOrder = state.imageOrder.filter(i => i !== id);
      const images = omit(state.images, id);
      return { ...state, imageOrder, images, uploadImageError: error };
    }
    case UPDATE_IMAGE_ORDER:
      return { ...state, imageOrder: payload.imageOrder };

    case REMOVE_PROJECT_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.images[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const images = omit(state.images, id);
      const imageOrder = state.imageOrder.filter(i => i !== id);

      return { ...state, images, imageOrder, removedImageIds };
    }

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case CHAT_APPEND_USER:
      return {
        ...state,
        jobPostingChat: {
          ...state.jobPostingChat,
          transcript: [...state.jobPostingChat.transcript, { role: 'user', text: payload.text }],
          error: null,
        },
      };
    case CHAT_STREAM_START:
      return {
        ...state,
        jobPostingChat: {
          ...state.jobPostingChat,
          isStreaming: true,
          streamingText: '',
          error: null,
        },
      };
    case CHAT_STREAM_DELTA:
      return {
        ...state,
        jobPostingChat: {
          ...state.jobPostingChat,
          streamingText: state.jobPostingChat.streamingText + payload.text,
        },
      };
    case CHAT_STREAM_DONE: {
      const { fullText, sessionId, done, projectDuration } = payload;
      return {
        ...state,
        jobPostingChat: {
          ...state.jobPostingChat,
          isStreaming: false,
          streamingText: '',
          transcript: [...state.jobPostingChat.transcript, { role: 'assistant', text: fullText }],
          sessionId: sessionId || state.jobPostingChat.sessionId,
          agent1Done: state.jobPostingChat.agent1Done || !!done,
          // Only the final message (with the sentinel) carries the marker; keep
          // any value already captured if a later event omits it.
          projectDuration: projectDuration || state.jobPostingChat.projectDuration,
        },
      };
    }
    case CHAT_SESSION_CREATED:
      return {
        ...state,
        jobPostingChat: { ...state.jobPostingChat, sessionId: payload.sessionId },
      };
    case CHAT_STREAM_ERROR:
      return {
        ...state,
        jobPostingChat: { ...state.jobPostingChat, isStreaming: false, error: payload },
      };
    case CHAT_HYDRATE:
      return {
        ...state,
        jobPostingChat: { ...state.jobPostingChat, ...payload },
      };
    case CHAT_RESET:
      return { ...state, jobPostingChat: { ...initialState.jobPostingChat } };

    case DRAFT_STREAM_START:
      return {
        ...state,
        jobPostingDraft: { ...initialState.jobPostingDraft, isStreaming: true },
      };
    case DRAFT_STREAM_DELTA:
      return {
        ...state,
        jobPostingDraft: {
          ...state.jobPostingDraft,
          streamingText: state.jobPostingDraft.streamingText + payload.text,
        },
      };
    case DRAFT_STREAM_DONE:
      return {
        ...state,
        jobPostingDraft: {
          ...state.jobPostingDraft,
          isStreaming: false,
          fields: payload.fields,
        },
      };
    case DRAFT_STREAM_ERROR:
      return {
        ...state,
        jobPostingDraft: { ...state.jobPostingDraft, isStreaming: false, error: payload },
      };
    case DRAFT_RESET:
      return { ...state, jobPostingDraft: { ...initialState.jobPostingDraft } };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const updateImageOrder = imageOrder => ({
  type: UPDATE_IMAGE_ORDER,
  payload: { imageOrder },
});

export const removeListingImage = imageId => ({
  type: REMOVE_PROJECT_IMAGE,
  payload: { imageId },
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraft = requestAction(CREATE_PROJECT_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_PROJECT_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_PROJECT_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListing = requestAction(PUBLISH_PROJECT_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_PROJECT_SUCCESS);
export const publishListingError = errorAction(PUBLISH_PROJECT_ERROR);

// SDK method: ownListings.update
export const updateListing = requestAction(UPDATE_PROJECT_REQUEST);
export const updateListingSuccess = successAction(UPDATE_PROJECT_SUCCESS);
export const updateListingError = errorAction(UPDATE_PROJECT_ERROR);

// SDK method: ownListings.show
export const showListings = requestAction(SHOW_PROJECTS_REQUEST);
export const showListingsSuccess = successAction(SHOW_PROJECTS_SUCCESS);
export const showListingsError = errorAction(SHOW_PROJECTS_ERROR);

// SDK method: images.upload
export const uploadImage = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// SDK method: bookings.query
export const fetchBookingsRequest = requestAction(FETCH_BOOKINGS_REQUEST);
export const fetchBookingsSuccess = successAction(FETCH_BOOKINGS_SUCCESS);
export const fetchBookingsError = errorAction(FETCH_BOOKINGS_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);

// SDK method: availabilityExceptions.create
export const createAvailabilityExceptionRequest = requestAction(CREATE_EXCEPTION_REQUEST);
export const createAvailabilityExceptionSuccess = successAction(CREATE_EXCEPTION_SUCCESS);
export const createAvailabilityExceptionError = errorAction(CREATE_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

// ================ Thunk ================ //

export function requestShowListing(actionPayload) {
  return (dispatch, getState, sdk) => {
    dispatch(showListings(actionPayload));
    return sdk.ownListings
      .show(actionPayload)
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

export function requestCreateListingDraft(data) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraft(data));

    data.publicData = data.publicData ? data.publicData : {};
    data.publicData.listingType = 'project';

    const queryParams = {
      expand: true,
      include: ['author', 'images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    };

    return sdk.ownListings
      .createDraft(data, queryParams)
      .then(response => {
        //const id = response.data.data.id.uuid;

        // Add the created listing to the marketplace data
        dispatch(addMarketplaceEntities(response));

        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(response));
        return response;
      })
      .then(response => {
        if (typeof window === 'object') {
          trackIntercomEvent('New job posting draft', {
            listingId: response.data.data.id.uuid,
          });
        }
        return response;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

export const requestPublishListingDraft = (listingId, currentUser) => (dispatch, getState, sdk) => {
  dispatch(publishListing(listingId));

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(response => {
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .then(response => {
      if (typeof window === 'object') {
        updateIntercomUser({
          projectListingCreated: true,
        });
      }
      return response;
    })
    .then(response => {
      if (typeof window === 'object') {
        trackIntercomEvent('New job posting published', {
          listingId: response.data.data.id.uuid,
        });
      }
      return response.data.data;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImage(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file })
      .then(resp => dispatch(uploadImageSuccess({ data: { id, imageId: resp.data.data.id } })))
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const requestFetchBookings = fetchParams => (dispatch, getState, sdk) => {
  const { listingId, start, end, state } = fetchParams;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);

  dispatch(fetchBookingsRequest({ ...fetchParams, monthId }));

  return sdk.bookings
    .query({ listingId, start, end, state }, { expand: true })
    .then(response => {
      const bookings = denormalisedResponseEntities(response);
      return dispatch(fetchBookingsSuccess({ data: { monthId, bookings } }));
    })
    .catch(e => {
      return dispatch(fetchBookingsError({ monthId, error: storableError(e) }));
    });
};

export const requestFetchAvailabilityExceptions = fetchParams => (dispatch, getState, sdk) => {
  const { listingId, start, end } = fetchParams;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);

  dispatch(fetchAvailabilityExceptionsRequest({ ...fetchParams, monthId }));

  return sdk.availabilityExceptions
    .query({ listingId, start, end }, { expand: true })
    .then(response => {
      const exceptions = denormalisedResponseEntities(response).map(availabilityException => ({
        availabilityException,
      }));
      return dispatch(fetchAvailabilityExceptionsSuccess({ data: { monthId, exceptions } }));
    })
    .catch(e => {
      return dispatch(fetchAvailabilityExceptionsError({ monthId, error: storableError(e) }));
    });
};

export const requestCreateAvailabilityException = params => (dispatch, getState, sdk) => {
  const { currentException, ...createParams } = params;

  dispatch(createAvailabilityExceptionRequest(createParams));

  return sdk.availabilityExceptions
    .create(createParams, { expand: true })
    .then(response => {
      dispatch(
        createAvailabilityExceptionSuccess({
          data: {
            exception: {
              availabilityException: response.data.data,
            },
          },
        })
      );
      return response;
    })
    .catch(error => {
      const availabilityException = currentException && currentException.availabilityException;
      return dispatch(
        createAvailabilityExceptionError({
          error: storableError(error),
          availabilityException,
        })
      );
    });
};

export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  const { currentException, seats, ...deleteParams } = params;

  dispatch(deleteAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .delete(deleteParams, { expand: true })
    .then(response => {
      dispatch(
        deleteAvailabilityExceptionSuccess({
          data: {
            exception: currentException,
          },
        })
      );
      return response;
    })
    .catch(error => {
      const availabilityException = currentException && currentException.availabilityException;
      return dispatch(
        deleteAvailabilityExceptionError({
          error: storableError(error),
          availabilityException,
        })
      );
    });
};

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
export function requestUpdateListing(tab, data) {
  return (dispatch, getState, sdk) => {
    dispatch(updateListing(data));
    const { id } = data;
    let updateResponse;
    return sdk.ownListings
      .update(data)
      .then(response => {
        updateResponse = response;
        const payload = {
          id,
          include: ['author', 'images'],
          'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
        };
        return dispatch(requestShowListing(payload));
      })
      .then(() => {
        dispatch(markTabUpdated(tab));
        dispatch(updateListingSuccess(updateResponse));
        return updateResponse;
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

// Save the current draft edits (title/description/budget) WITHOUT publishing.
// The listing already exists as a draft (created on chat-continue), so this is a
// plain update. Rejects on failure so the caller only redirects on success.
export const requestSaveListingDraft = data => (dispatch, getState, sdk) => {
  dispatch({ type: SAVE_LISTING_DRAFT_REQUEST });

  return sdk.ownListings
    .update(data, { expand: true })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch({ type: SAVE_LISTING_DRAFT_SUCCESS });
      return response;
    })
    .catch(e => {
      log.error(e, 'save-listing-draft-failed', { listingData: data });
      dispatch({ type: SAVE_LISTING_DRAFT_ERROR, payload: storableError(e), error: true });
      throw e;
    });
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = params => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  const { id, type, tab } = params;

  if (type === 'new') {
    // Starting a brand-new listing: clear any chat conversation and draft left
    // over from a previous listing so they can't bleed into this one. A "new"
    // listing has no persisted state, so its chat must always start empty. This
    // branch only runs on the initial entry to /new/chat — once the first tab is
    // completed the URL is replaced with a "draft" type (see redirectAfterDraftUpdate),
    // so an in-progress conversation is never reset here.
    dispatch(resetChat());
    dispatch(resetDraft());
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser())])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = {
    id: new UUID(id),
    include: ['author', 'images'],
    'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  };

  return Promise.all([dispatch(requestShowListing(payload)), dispatch(fetchCurrentUser())])
    .then(response => {
      const currentUser = getState().user.currentUser;
      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }
      const listing = response[0]?.data?.data;
      const stored = listing?.attributes?.privateData?.jobPostingChat;
      if (
        stored &&
        ((Array.isArray(stored.transcript) && stored.transcript.length > 0) || stored.sessionId)
      ) {
        dispatch({
          type: CHAT_HYDRATE,
          payload: {
            transcript: Array.isArray(stored.transcript) ? stored.transcript : [],
            sessionId: stored.sessionId || null,
            agent1Done: !!stored.agent1Done,
            projectDuration: stored.projectDuration || null,
          },
        });
      } else {
        // The loaded listing has no chat context (a published listing, or a draft
        // edited from elsewhere). Reset so the chat we now preserve across
        // SHOW_PROJECTS_SUCCESS can't bleed in from a previously-open listing.
        dispatch(resetChat());
      }

      // Draft generation. The writer needs the chat transcript + budget, both
      // persisted on the listing. Trigger it as early as possible — when the
      // draft step loads — so its output is already streaming by the time the
      // panel renders. An in-flight stream started on "continue" is preserved
      // across this load (see SHOW_PROJECTS_SUCCESS) and not re-triggered.
      const publicData = listing?.attributes?.publicData || {};
      const transcript = Array.isArray(stored?.transcript) ? stored.transcript : [];
      const manualFill = !!(stored && stored.manualFill);
      const hasDescription = !!publicData.descriptionText;
      const validDraftContext = transcript.length > 0 && !manualFill && !hasDescription;

      if (!validDraftContext) {
        // Editing a published listing, a manual-fill draft, or a listing with no
        // chat context — make sure no draft from a previous listing lingers.
        dispatch(resetDraft());
      } else if (tab === 'draft') {
        const draftState = getState().EditProjectListingPage.jobPostingDraft;
        if (!draftState.isStreaming && !draftState.fields) {
          dispatch(
            generateDraft({
              transcript,
              budget: {
                priceEstimate: publicData.priceEstimate,
                projectDuration: publicData.projectDuration,
                projectUrgency: publicData.projectUrgency,
              },
            })
          );
        }
      }
      return response;
    })
    .catch(e => {
      throw e;
    });
};

// ================ Job posting LLM thunks ================ //

export const resetChat = () => ({ type: CHAT_RESET });
export const resetDraft = () => ({ type: DRAFT_RESET });

export const sendChatMessage = userText => (dispatch, getState) => {
  if (!userText || !userText.trim()) return Promise.resolve();

  dispatch({ type: CHAT_APPEND_USER, payload: { text: userText.trim() } });
  dispatch({ type: CHAT_STREAM_START });

  const sessionId = getState().EditProjectListingPage.jobPostingChat.sessionId || undefined;

  return new Promise(resolve => {
    jobPostingChatStream(
      { userMessage: userText.trim(), sessionId },
      {
        onEvent: (eventName, data) => {
          if (eventName === 'session') {
            dispatch({ type: CHAT_SESSION_CREATED, payload: { sessionId: data.sessionId } });
          } else if (eventName === 'delta') {
            dispatch({ type: CHAT_STREAM_DELTA, payload: { text: data.text || '' } });
          } else if (eventName === 'done') {
            dispatch({ type: CHAT_STREAM_DONE, payload: data });
          } else if (eventName === 'error') {
            dispatch({ type: CHAT_STREAM_ERROR, payload: storableError(new Error(data.message)) });
          }
        },
        onDone: () => resolve(),
        onError: err => {
          dispatch({ type: CHAT_STREAM_ERROR, payload: storableError(err) });
          resolve();
        },
      }
    );
  });
};

// Read an own-listing entity straight from the normalized store. Used by the
// mid-chat autosave thunks to merge into existing privateData/publicData without
// a round-trip fetch.
const ownListingFromState = (state, id) => {
  if (!id) return null;
  const entities = getMarketplaceEntities(state, [{ id, type: 'ownListing' }]);
  return entities.length === 1 ? entities[0] : null;
};

// Guards against two near-simultaneous turns both creating a draft before the
// first create resolves (submittedListingId would still be null for the second).
let creatingDraftInFlight = false;

// Mid-chat persistence: create the draft on the first completed exchange, then
// update it on every subsequent turn so an unfinished interview survives leaving
// the page / reloading and can be resumed from the chat panel. Reuses
// requestSaveListingDraft for updates (a plain update with NO re-fetch) so the
// live transcript in Redux is never wiped mid-conversation.
//
// The caller passes the current listing id (the panel resolves it from
// submittedListingId or the URL). We can't read submittedListingId here because
// the new->draft URL replace fires a loadData that resets it (the URL id then
// becomes the source of truth — see SHOW_PROJECTS_SUCCESS).
export const persistChatTurn = ({ titleSeed, listingId } = {}) => (dispatch, getState) => {
  const chat = getState().EditProjectListingPage.jobPostingChat;
  // Only persist a settled turn — never mid-stream, and only once there's at
  // least one full user/assistant exchange.
  if (chat.isStreaming) return Promise.resolve();
  if (!Array.isArray(chat.transcript) || chat.transcript.length < 2) return Promise.resolve();

  const jobPostingChat = {
    transcript: chat.transcript,
    sessionId: chat.sessionId,
    agent1Done: chat.agent1Done,
    projectDuration: chat.projectDuration,
  };

  if (!listingId) {
    if (creatingDraftInFlight) return Promise.resolve();
    creatingDraftInFlight = true;
    return dispatch(
      requestCreateListingDraft({ title: titleSeed, privateData: { jobPostingChat } })
    ).finally(() => {
      creatingDraftInFlight = false;
    });
  }

  // Merge into existing privateData so unrelated keys survive.
  const existing = ownListingFromState(getState(), listingId);
  const existingPrivate = (existing && existing.attributes.privateData) || {};
  return dispatch(
    requestSaveListingDraft({ id: listingId, privateData: { ...existingPrivate, jobPostingChat } })
  );
};

// Persist the budget selections (urgency / price) the moment they're picked —
// including the first of the two, so a user who answers only one and leaves
// resumes on the remaining step (seedBudgetValues/seedBudgetStep read these back
// from publicData). Only defined picks are written so a partial answer never
// clears the other field.
export const persistChatBudget = ({ listingId, budget }) => (dispatch, getState) => {
  if (!listingId) return Promise.resolve();
  const existing = ownListingFromState(getState(), listingId);
  const existingPublic = (existing && existing.attributes.publicData) || {};
  const cleaned = Object.keys(budget || {}).reduce((acc, key) => {
    if (budget[key] !== undefined && budget[key] !== null) acc[key] = budget[key];
    return acc;
  }, {});
  return dispatch(
    requestSaveListingDraft({ id: listingId, publicData: { ...existingPublic, ...cleaned } })
  );
};

// Seeds the chat with the BRIEF answer as the first user message, then triggers agent 1.
export const startChatWithBrief = brief => dispatch => {
  dispatch({ type: CHAT_RESET });
  return dispatch(sendChatMessage(brief));
};

// Caller (DraftPanel) passes { transcript, budget } pulled from the listing's
// privateData — the source of truth that survives tab transitions and reloads.
// Pulling from Redux directly is unsafe because SHOW_PROJECTS_SUCCESS resets
// the page slice on every loadData, before hydration races back in.
export const generateDraft = ({ transcript = [], budget = {} } = {}) => dispatch => {
  dispatch({ type: DRAFT_STREAM_START });

  return new Promise(resolve => {
    jobPostingDraftStream(
      {
        transcript,
        budget,
      },
      {
        onEvent: (eventName, data) => {
          if (eventName === 'delta') {
            dispatch({ type: DRAFT_STREAM_DELTA, payload: { text: data.text || '' } });
          } else if (eventName === 'done') {
            dispatch({ type: DRAFT_STREAM_DONE, payload: data });
          } else if (eventName === 'error') {
            dispatch({ type: DRAFT_STREAM_ERROR, payload: storableError(new Error(data.message)) });
          }
        },
        onDone: () => resolve(),
        onError: err => {
          dispatch({ type: DRAFT_STREAM_ERROR, payload: storableError(err) });
          resolve();
        },
      }
    );
  });
};
